import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { LoginInput, SessionUser } from '@ai-sales/shared'
import { createContext, use, type ReactNode } from 'react'
import { fetchMe, login as loginRequest, logout as logoutRequest } from '../api/auth.api.js'
import { ApiRequestError } from '../lib/api-client.js'

/**
 * Текущая сессия.
 *
 * Источник правды — сервер, а не локальное хранилище. Токен сессии лежит
 * в httpOnly-cookie, которую JavaScript прочитать не может, поэтому единственный
 * способ узнать, вошёл ли человек, — спросить у /auth/me. Это на один запрос
 * дороже при загрузке страницы и полностью честно: интерфейс не может
 * считать себя авторизованным, когда сессия на сервере уже отозвана.
 *
 * Обратная сторона: пока запрос идёт, состояние неизвестно. Поэтому
 * `status` различает загрузку и отсутствие сессии — без этого при каждой
 * перезагрузке страницы человека на мгновение выбрасывало бы на вход.
 */
export interface SessionState {
  status: 'loading' | 'authenticated' | 'anonymous'
  user: SessionUser | null
  /** Ошибка, не связанная с отсутствием сессии: сеть, сбой сервера. */
  error: unknown
  refetch: () => void
}

const SessionContext = createContext<SessionState | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const query = useQuery({
    queryKey: ['session'],
    queryFn: fetchMe,
    /**
     * 401 — это не сбой, а ответ «не вошёл». Повторять его бессмысленно,
     * а в остальных случаях одна повторная попытка уместна: чаще всего
     * это моргнувшая сеть.
     */
    retry: (failureCount, error) =>
      error instanceof ApiRequestError && error.isUnauthorized ? false : failureCount < 1,
    staleTime: 30_000,
  })

  const unauthorized = query.error instanceof ApiRequestError && query.error.isUnauthorized

  const state: SessionState = {
    status: query.isPending ? 'loading' : query.data ? 'authenticated' : 'anonymous',
    user: query.data ?? null,
    error: unauthorized ? null : query.error,
    refetch: () => void query.refetch(),
  }

  return <SessionContext value={state}>{children}</SessionContext>
}

export function useSession(): SessionState {
  const state = use(SessionContext)

  if (!state) {
    throw new Error('useSession вызван вне SessionProvider')
  }

  return state
}

/**
 * Пользователь, о существовании которого код уже знает.
 * Вызывается только внутри закрытых маршрутов, где RequireAuth уже
 * убедился в наличии сессии, — поэтому здесь можно не проверять null
 * на каждом экране.
 */
export function useCurrentUser(): SessionUser {
  const { user } = useSession()

  if (!user) {
    throw new Error('useCurrentUser вызван вне закрытого маршрута')
  }

  return user
}

export function useLogin() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: LoginInput) => loginRequest(input),
    onSuccess: (user) => {
      // Сессия известна сразу, лишний запрос /auth/me не нужен
      queryClient.setQueryData(['session'], user)
    },
  })
}

export function useLogout() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: logoutRequest,
    /**
     * onSettled, а не onSuccess: если выход не удался из-за сети, локальное
     * состояние всё равно надо очистить. Человек нажал «выйти» — он не должен
     * остаться в интерфейсе с данными, которые считает закрытыми.
     */
    onSettled: () => {
      queryClient.setQueryData(['session'], null)
      void queryClient.invalidateQueries()
    },
  })
}
